import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { useTranslation } from 'react-i18next';
import Legend from '../Legend';
import AreaGradient from '../Common/Charts/LineChartAreaGradient';
import { DashboardCard } from '../Common/SurfaceCard';

interface HourlyEntriesProps {
    entriesPerHourXValues: number[];
    entriesPerHourYValues: {
        today: number[];
        yesterday: number[];
        lastWeek: number[];
    };
}

const ChartContainer = styled(Box)`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-height: ${({ theme }) => theme.spacing(30)};
    ${({ theme }) => theme.breakpoints.up('md')} {
        min-height: 0;
    }
`;

export default function HourlyEntries({ entriesPerHourXValues, entriesPerHourYValues }: HourlyEntriesProps) {
    const { t } = useTranslation("common");
    const theme = useTheme();

    const colorPalette = [
        theme.palette.primary.light,
        theme.palette.primary.main,
        theme.palette.primary.dark,
    ];

    return (
        <DashboardCard title={t("entries_per_hour_title")}>
            <Legend
                items={[
                    { color: theme.palette.primary.light, label: t("linechart_hours_set1") },
                    { color: theme.palette.primary.main, label: t('linechart_hours_set2') },
                    { color: theme.palette.primary.dark, label: t('linechart_hours_set3') },
                ]}
            />
            <ChartContainer>
                <LineChart
                    colors={colorPalette}
                    xAxis={[
                        {
                            data: entriesPerHourXValues,
                            scaleType: 'point',
                        },
                    ]}
                    series={[
                        {
                            id: 'today',
                            label: t("linechart_hours_set1"),
                            showMark: false,
                            curve: 'linear',
                            area: true,
                            data: entriesPerHourYValues.today,
                            valueFormatter: value => `${value || 0} ${t("cars")}`,
                        },
                        {
                            id: 'yesterday',
                            label: t("linechart_hours_set2"),
                            showMark: false,
                            curve: 'linear',
                            data: entriesPerHourYValues.yesterday,
                            valueFormatter: value => `${value || 0} ${t("cars")}`,
                        },
                        {
                            id: 'lastweek',
                            label: t("linechart_hours_set3"),
                            showMark: false,
                            curve: 'linear',
                            data: entriesPerHourYValues.lastWeek,
                            valueFormatter: value => `${value || 0} ${t("cars")}`,
                        },
                    ]}
                    margin={{ left: 50, right: 20, top: 20, bottom: 20 }}
                    grid={{ horizontal: true, vertical: true }}
                    sx={{
                        '& .MuiAreaElement-series-yesterday': { fill: "url('#yesterday')" },
                        '& .MuiAreaElement-series-lastweek': { fill: "url('#lastweek')" },
                        '& .MuiAreaElement-series-today': { fill: "url('#today')" },
                    }}
                    slotProps={{ legend: { hidden: true } }}
                >
                    <AreaGradient color={theme.palette.primary.light} id="today" />
                    <AreaGradient color={theme.palette.primary.main} id="yesterday" />
                    <AreaGradient color={theme.palette.primary.dark} id="lastweek" />
                </LineChart>
            </ChartContainer>
        </DashboardCard>
    );
}
